import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot, getFirestore } from 'firebase/firestore';
import { Message, PaymentStatus, normalizePaymentStatus } from '../models/Message';
import { useAuth } from '../context/AuthContext';

export const usePaymentStatus = (message: Message, formId?: string) => {
  const { userId } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    normalizePaymentStatus(message.paymentData?.status)
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the latest status in a ref so the snapshot callback can compare
  // against it without paymentStatus being an effect dependency — otherwise
  // every status change tears down and re-creates the onSnapshot listener.
  const paymentStatusRef = useRef(paymentStatus);
  paymentStatusRef.current = paymentStatus;

  useEffect(() => {
    if (!formId || !message.paymentData || !userId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const db = getFirestore();
    const messageRef = doc(db, `public_forms/${formId}/activities/messages/${userId}`, message.id);

    const unsubscribe = onSnapshot(
      messageRef, 
      (doc) => {
        try {
          if (doc.exists()) {
            const data = doc.data();
            const newStatus = data.paymentData?.status ? normalizePaymentStatus(data.paymentData.status) : null;
            
            if (newStatus && newStatus !== paymentStatusRef.current) {
              setPaymentStatus(newStatus);
            }
          }
          setIsLoading(false);
        } catch (err: any) {
          console.error('Error in payment status listener:', err);
          setError(err.message || 'Failed to monitor payment status');
          setIsLoading(false);
        }
      },
      (err) => {
        console.error('Payment status listener error:', err);
        setError(err.message || 'Failed to set up payment monitoring');
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [formId, message.id, message.paymentData, userId]);

  return {
    paymentStatus,
    isLoading,
    error,
  };
};

// Hook for monitoring multiple payment messages
export const usePaymentStatusList = (messages: Message[], formId?: string) => {
  const { userId } = useAuth();
  const [paymentStatuses, setPaymentStatuses] = useState<Record<string, PaymentStatus>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!formId || !userId || messages.length === 0) {
      return;
    }

    const paymentMessages = messages.filter(msg => msg.paymentData);
    
    if (paymentMessages.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const db = getFirestore();
    const unsubscribes: (() => void)[] = [];

    // Set up listeners for each payment message
    paymentMessages.forEach(message => {
      const messageRef = doc(db, `public_forms/${formId}/activities/messages/${userId}`, message.id);
      
      const unsubscribe = onSnapshot(
        messageRef,
        (doc) => {
          try {
            if (doc.exists()) {
              const data = doc.data();
              const newStatus = data.paymentData?.status ? normalizePaymentStatus(data.paymentData.status) : null;
              
              if (newStatus) {
                setPaymentStatuses(prev => ({
                  ...prev,
                  [message.id]: newStatus
                }));
              }
            }
          } catch (err: any) {
            console.error(`Error monitoring payment ${message.id}:`, err);
            setError(prev => prev || err.message);
          }
        },
        (err) => {
          console.error(`Payment listener error for ${message.id}:`, err);
          setError(prev => prev || err.message);
        }
      );

      unsubscribes.push(unsubscribe);
    });

    setIsLoading(false);

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [formId, userId, messages]);

  const getPaymentStatus = (messageId: string): PaymentStatus => {
    return paymentStatuses[messageId] || PaymentStatus.OPEN;
  };

  return {
    paymentStatuses,
    getPaymentStatus,
    isLoading,
    error,
  };
};